/**
 * Pure header value validation — rejects strings containing control characters
 * or characters outside the ISO-8859-1 printable range, which would otherwise
 * cause WebKit `fetch()` to throw "The string did not match the expected
 * pattern" (Sentry D5/DN, issue #3255).
 *
 * @returns `{ valid: true }` for clean values, or `{ valid: false; reason }`
 *          with the character class that triggered rejection.
 */

export interface HeaderValidationResult {
  valid: boolean;
  reason?: "control" | "non-latin1";
}

/**
 * ISO-8859-1 (Latin-1) printable range:
 *  - U+0020–U+007E (ASCII printable: space through ~)
 *  - U+00A0–U+00FF (Latin-1 Supplement printable: NBSP through ÿ)
 *
 * Control characters (U+0000–U+001F, U+007F DEL) are rejected, as are all
 * characters outside the Latin-1 block — these would turn a fetch() header
 * into a malformed input in WebKit.
 */

function isLatin1Printable(code: number): boolean {
  return (code >= 0x20 && code <= 0x7e) || (code >= 0xa0 && code <= 0xff);
}

function isControl(code: number): boolean {
  return (code >= 0x00 && code <= 0x1f) || code === 0x7f;
}

export function validateHeaderValue(value: string): HeaderValidationResult {
  const trimmed = value.trim();
  if (trimmed.length === 0) return { valid: true };

  for (const ch of trimmed) {
    const code = ch.codePointAt(0)!;
    if (isControl(code)) return { valid: false, reason: "control" };
    if (!isLatin1Printable(code)) return { valid: false, reason: "non-latin1" };
  }
  return { valid: true };
}
